import type { Address, OpeningHours } from "api-types";

import { e164FromFormattedPhoneNumber } from "igfd-ui/src/phone-number";

import { cleanWebsiteField } from "@dashboard/utils/cleanWebsiteField";

import { isOpeningHoursValid } from "../opening-hours/OpeningHoursInput";

type OpeningHourTime = {
  day: number;
  time: string;
  hours: number;
  minutes: number;
};
export type PlaceDetailResult = {
  address_components: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
  name: string;
  geometry: {
    location: {
      lat: () => number;
      lng: () => number;
    };
  };
  opening_hours: {
    open_now: boolean;
    weekday_text: string[];
    periods: { close: OpeningHourTime; open: OpeningHourTime }[];
  };
  price_level: number;
  website: string;
  international_phone_number: string;
};

// A restaurant found from google maps
// This model follows the ingredifind restaurant model
export type FoundRestaurant = {
  name: string;
  address: Address;

  additionalInfo: {
    website: string | null;
    priceRange: number | null;
    geoLocation: {
      latitute: number;
      longitude: number;
    };

    // Readable version returned by google and shown to the user.
    weekDayOpeningHours: string[];
    // ingredifind version of opening hours, sent to the backend.
    openingHours: OpeningHours[];

    // phone number fields
    e164PhoneNumber: string | null;
    formattedPhoneNumber: string | null;
  };
};

export const parsePlaceDetailResult = (
  result: PlaceDetailResult
): FoundRestaurant => {
  const streetNumber = result.address_components.find((c) =>
    c.types.includes("street_number")
  )?.long_name;

  const streetName = result.address_components.find((c) =>
    c.types.includes("route")
  )?.long_name;

  const locality = result.address_components.find((c) =>
    c.types.includes("locality")
  )?.long_name;
  const postalTown = result.address_components.find((c) =>
    c.types.includes("postal_town")
  )?.long_name;

  const postalCode = result.address_components.find((c) =>
    c.types.includes("postal_code")
  )?.long_name;
  const countryCode = result.address_components
    .find((c) => c.types.includes("country"))
    ?.short_name.toUpperCase();

  const address = {
    street:
      streetNumber && streetName ? `${streetNumber} ${streetName}` : streetName,
    city: locality || postalTown || "",
    postalCode,
    countryCode: countryCode || "",
  };

  const cleanedWebsite =
    (result.website && cleanWebsiteField(result.website)) || null;

  // the international phone number is in the format +1 123-456-7890 (Formatted in our model.)
  const formattedPhoneNumber = result.international_phone_number;
  const e164PhoneNumber = e164FromFormattedPhoneNumber(formattedPhoneNumber);

  const openingHours = result.opening_hours
    ? toOpeningHours(result.opening_hours.periods)
    : [];

  // only show the text representation if the opening hours are valid based on our model.
  const weekDayOpeningHours =
    openingHours.length > 0 ? result.opening_hours?.weekday_text || [] : [];

  return {
    name: result.name,
    address,
    additionalInfo: {
      website: cleanedWebsite,
      priceRange: toPriceRange(result.price_level),
      geoLocation: {
        latitute: result.geometry.location.lat(),
        longitude: result.geometry.location.lng(),
      },

      weekDayOpeningHours,
      openingHours,

      e164PhoneNumber,
      formattedPhoneNumber,
    },
  };
};

/**
 * priceLevel is a term used by google maps to indicate the price range of a restaurant.
 * priceLevel values are as follows:
 * 0 - Free
 * 1 - Inexpensive
 * 2 - Moderate
 * 3 - Expensive
 * 4 - Very Expensive
 *
 * Ingredifind uses a price range from 1 to 4, where 1 is the cheapest and 4 is the most expensive.
 *
 * Our mapping logic is as follows:
 * 0 -> 1
 * 1 -> 1
 * 2 -> 2
 * 3 -> 3
 * 4 -> 4
 * @param priceLevel
 */
const toPriceRange = (priceLevel: number | undefined | null): number | null => {
  if (priceLevel === undefined || priceLevel === null) {
    return null;
  }
  if (priceLevel === 0) {
    return 1;
  }
  return priceLevel;
};

const toOpeningHours = (
  periods: PlaceDetailResult["opening_hours"]["periods"]
): OpeningHours[] => {
  const isoDays = Array.from({ length: 7 }, (_, i) => i + 1);

  const openingHours = isoDays.map((day) => {
    // google maps day of week is 0-6 starting with sunday.
    // ingredifind uses ISO-8601 day of week 1-7 starting with monday.
    const googleMapDay = day - 1;
    const periodsForDay = periods.filter(
      (period) => period.open.day === googleMapDay
    );
    const timeSlots = periodsForDay.map((period) => ({
      startHour: period.open.hours,
      startMin: period.open.minutes,
      endHour: period.close.hours,
      endMin: period.close.minutes,
    }));
    return {
      isoDayOfWeeks: [day],
      timeSlots,
    };
  });

  // validate the opening hours to match our model.
  // other wise don't return any opening hours.
  if (!isOpeningHoursValid(openingHours)) {
    return [];
  }
  return openingHours;
};

export const formatAdditionalInfo = (
  additionalInfo: FoundRestaurant["additionalInfo"]
): string => {
  if (!additionalInfo) {
    return "";
  }
  // keep the fields website, priceRance and openingHours as info for now.
  const infos = [
    additionalInfo.priceRange
      ? `Price range: ${additionalInfo.priceRange}`
      : "",
    additionalInfo.website ? `Website: ${additionalInfo.website}` : "",
    additionalInfo.formattedPhoneNumber
      ? `Phone number: ${additionalInfo.formattedPhoneNumber}`
      : "",
    additionalInfo.weekDayOpeningHours &&
    additionalInfo.weekDayOpeningHours.length > 0
      ? `Opening hours: ${additionalInfo.weekDayOpeningHours.join("\n")}`
      : "",
  ].filter(Boolean);
  return infos.join("\n");
};
